import { useEffect, useRef, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { QRCodeCanvas } from 'qrcode.react';
import { 
  QrCode, 
  Download, 
  Share2, 
  Smartphone,
  Copy,
  Check,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

export default function DownloadApp() {
  const [copied, setCopied] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const qrRef = useRef(null);
  const appUrl = typeof window !== 'undefined' ? window.location.origin : 'https://vaultride.ch';
  const logoUrl = typeof window !== 'undefined' ? `${window.location.origin}/logo.png` : '/logo.png';

  useEffect(() => {
    const img = new Image();
    img.onload = () => setLogoLoaded(true);
    img.src = logoUrl;
  }, [logoUrl]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(appUrl);
    setCopied(true);
    toast.success('Link copiato!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Vault Ride - App per Motociclisti',
          text: 'Scarica Vault Ride, l\'app per gestire le tue moto!',
          url: appUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  };

  const handleDownloadQR = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = url;
      link.download = 'vaultride-qr-code.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('QR Code scaricato!');
    }
  };

  return (
    <Layout>
      <div className="page-container max-w-2xl mx-auto">
        <div className="vault-header mb-8 text-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-16 h-16 rounded-full bg-[#FF5F1F]/20 flex items-center justify-center">
              <QrCode className="w-8 h-8 text-[#FF5F1F]" />
            </div>
            <div>
              <h1 className="vault-title">Scarica l'App</h1>
              <p className="text-[#9CA3AF]">Scansiona il QR code con il tuo smartphone</p>
            </div>
          </div>
        </div>

        <div className="bg-[#121212] border border-[#262626] rounded-lg p-8 text-center mb-6">
          <div ref={qrRef} className="inline-block p-4 bg-[#0a0a0a] rounded-lg mb-6">
            <QRCodeCanvas
              value={appUrl}
              size={256}
              level="H"
              includeMargin={false}
              imageSettings={logoLoaded ? {
                src: logoUrl,
                height: 50,
                width: 50,
                excavate: true,
              } : undefined}
              bgColor="#0a0a0a"
              fgColor="#FF5F1F"
            />
          </div>
          
          <h2 className="text-white font-['Teko'] text-2xl uppercase mb-2">VAULT RIDE</h2>
          <p className="text-[#9CA3AF] text-sm mb-6">L'app definitiva per i motociclisti</p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={handleDownloadQR} variant="outline" className="border-[#262626] text-white">
              <Download className="w-4 h-4 mr-2" />
              Scarica QR
            </Button>
            <Button onClick={handleShare} className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90">
              <Share2 className="w-4 h-4 mr-2" />
              Condividi
            </Button>
          </div>
        </div>

        <div className="bg-[#121212] border border-[#262626] rounded-lg p-6 mb-6">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <ExternalLink className="w-4 h-4 text-[#FF5F1F]" />
            Link Diretto
          </h3>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-[#0a0a0a] border border-[#262626] rounded px-4 py-3 font-mono text-sm text-[#9CA3AF] truncate">
              {appUrl}
            </div>
            <Button onClick={handleCopyLink} variant="outline" className="border-[#262626] text-white shrink-0">
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        <div className="bg-[#121212] border border-[#262626] rounded-lg p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-[#FF5F1F]" />
            Come installare sul telefono
          </h3>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-[#FF5F1F]/20 flex items-center justify-center text-[#FF5F1F] font-bold shrink-0">1</div>
              <div>
                <p className="text-white font-medium">Scansiona il QR Code</p>
                <p className="text-[#666] text-sm">Usa la fotocamera del tuo smartphone</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-[#FF5F1F]/20 flex items-center justify-center text-[#FF5F1F] font-bold shrink-0">2</div>
              <div>
                <p className="text-white font-medium">Apri il link</p>
                <p className="text-[#666] text-sm">Si aprirà Vault Ride nel browser</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-[#FF5F1F]/20 flex items-center justify-center text-[#FF5F1F] font-bold shrink-0">3</div>
              <div>
                <p className="text-white font-medium">Aggiungi alla Home</p>
                <p className="text-[#666] text-sm">
                  <strong>iPhone:</strong> Tocca condividi → "Aggiungi a Home"<br/>
                  <strong>Android:</strong> Menu → "Aggiungi a schermata Home"
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-[#666] text-sm">Vault Ride è una Progressive Web App (PWA) che funziona su tutti i dispositivi.</p>
        </div>
      </div>
    </Layout>
  );
}
